import { Modal, Button as AntButton, Space } from 'antd';
import { useState } from 'react';
import { Gallery } from './components/Gallery';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { ThemeScope } from '@/theme/ThemeScope';
import { THEME_MODES, type ThemeMode } from '@/theme/tokens';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  THE DESIGN GALLERY — /design. Off the navigation, deliberately.
 *
 *  This is P0's `/demo` grown up. It is two things at once, and both matter:
 *
 *   1. THE REVIEW SURFACE for this pass. Every token, every primitive and every restyled
 *      AntD component, in BOTH THEMES ON ONE PAGE — because a gallery you have to toggle
 *      to compare is a gallery nobody compares. `ThemeScope` makes that work; read its
 *      header for how, and for the three things it cannot do.
 *
 *   2. THE COEXISTENCE PROOF it inherited. Three styling systems in one app is the most
 *      likely thing to be quietly wrong here, and the failure is VISUAL rather than a
 *      build error. Three claims stay falsifiable on sight:
 *
 *        A. AntD survives preflight   — the tables have borders and the buttons have a
 *                                       background. If preflight won they would be
 *                                       unstyled boxes.
 *        B. Tailwind overrides AntD   — the "Tailwind wins" button below is an AntD
 *                                       primary button wearing Tailwind utilities. If the
 *                                       layer order were wrong it would ignore them.
 *        C. Both overlay systems work — an AntD Modal and a Radix/shadcn Dialog open
 *                                       together, neither behind the other.
 *
 *  It stays off `NAV_SECTIONS`: the nav is domain navigation gated by access role, and
 *  this is a developer page.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
const SCOPE_LABEL: Record<ThemeMode, string> = {
  light: 'Light — white surfaces on a barely-tinted canvas',
  dark: 'Dark — near-black canvas, elevation by surface lightness',
};

const DesignPage = () => {
  const [antdModalOpen, setAntdModalOpen] = useState(false);

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">Design gallery</h1>
        <p className="text-sm text-muted-foreground">
          Every token and primitive, in both themes at once. Off the navigation on purpose — this is the review surface
          for the design language and the regression surface for the three styling systems.
        </p>
      </header>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold tracking-tight">Overlay coexistence</h2>
        <p className="text-sm text-muted-foreground">
          Open both. Neither may vanish behind the other, and the AntD Modal must sit above the page rather than under
          it — that is the z-index half of the arrangement, which no unit test can see.
        </p>
        <Space wrap>
          <AntButton type="primary" onClick={() => setAntdModalOpen(true)}>
            Open AntD Modal
          </AntButton>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline">Open shadcn Dialog</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>shadcn dialog</DialogTitle>
                <DialogDescription>
                  Radix portals this to &lt;body&gt;, outside the React tree — the same thing AntD does, which is why
                  they are worth seeing open together.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline">Close</Button>
                </DialogClose>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/*
            CLAIM B, on one line: an AntD primary button wearing Tailwind utilities, with
            NO important modifier anywhere. If it renders green, `@layer utilities` really
            does outrank `@layer antd` and the coexistence arrangement holds. If it renders
            in the primary fill, the layer order or `<StyleProvider layer>` has broken —
            and that is the exact failure this page exists to make visible, because it
            produces a green build.
          */}
          <AntButton type="primary" className="bg-success text-background">
            Tailwind wins
          </AntButton>
        </Space>

        <Modal
          title="AntD modal"
          open={antdModalOpen}
          onOk={() => setAntdModalOpen(false)}
          onCancel={() => setAntdModalOpen(false)}
        >
          <p>
            Rendered through AntD&apos;s own portal and themed from <code>theme/tokens.ts</code>. In dark mode it must
            have no drop shadow at all — elevation is the raised surface plus a hairline.
          </p>
        </Modal>
      </section>

      <div className="grid gap-6 xl:grid-cols-2">
        {THEME_MODES.map((mode) => (
          <div key={mode} className="overflow-hidden rounded-xl border border-border">
            <div className="border-b border-border bg-card px-4 py-2 text-xs font-medium text-muted-foreground">
              {SCOPE_LABEL[mode]}
            </div>
            <ThemeScope mode={mode}>
              <Gallery mode={mode} />
            </ThemeScope>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DesignPage;
